import React, { useRef, useState } from 'react';
import { Transition, TransitionGroup } from 'react-transition-group';
import { Text, TextVariant } from '@naf/text';
import { Button } from '@naf-simba/elements-buttons';
import { PinDrop } from '@naf-simba/building-blocks-icons/Location';
import { UserInteraction, UserInteractionVariant } from '@naf/user-interaction';
import { useDispatch } from 'react-redux';
import { ArrowForward } from '@styled-icons/material/ArrowForward';
import { AnimationWrapper } from '../../../../components/skeleton-loaders/AnimationWrapper';
import { SelectLoader } from '../../../../components/skeleton-loaders/SelectLoader';
import { actions } from '../../../../redux/modules/centers';
import useSelector from '../../../../redux/typedHooks';
import { CenterType } from '../../../../../../types/centerType';
import { formatDistance, findNearestCenter } from './CenterSelect.utils';
import { useTrackCTA } from '../../../../utils/Tracking/CtaClick/TrackCtaClick';
import * as S from './CenterSelect.styles';

type Props = {
  centerId?: number;
  handleSelect: (option: Record<string, string>) => void;
  selectedCenter?: CenterType;
  slug: string;
};

const CenterSelect = ({ centerId, handleSelect, selectedCenter, slug }: Props) => {
  const dispatch = useDispatch();
  const {
    centerList: { isUpdating, data, error },
  } = useSelector((state) => state.centers);
  const selectRef = useRef<HTMLDivElement | null>(null);
  const [isFindingLocation, setIsFindingLocation] = useState(false);
  const [locationError, setLocationError] = useState<string | null>(null);
  const [sortedCenters, setSortedCenters] = useState<CenterType[] | null>(null);
  const trackCTA = useTrackCTA();

  const centerList = sortedCenters || data;
  const options =
    centerList &&
    !isUpdating &&
    centerList.map((center) => ({
      label: `${center.title || ''}${formatDistance(center.distanceInMeters)}`,
      value: center.centerId.toString(),
    }));

  const isSelectReady = !!(data && !isUpdating && options);

  const selectedCenterWithDistance = centerId
    ? centerList?.find((center) => center.centerId === centerId) || selectedCenter
    : undefined;

  const handleFindNearestCenter = async () => {
    trackCTA({
      cta_position: 'center_select',
      text: 'Finn nærmeste NAF Senter',
      url: window.location.href,
    });

    setIsFindingLocation(true);
    setLocationError(null);

    try {
      const { nearbyCenters, nearestCenter } = await findNearestCenter();

      if (nearestCenter) {
        setSortedCenters(nearbyCenters);
        handleSelect({
          value: nearestCenter.centerId.toString(),
          label: nearestCenter.title || '',
        });
      } else {
        setLocationError('Kunne ikke finne nærmeste senter.');
      }
    } catch (err) {
      const errorMessage =
        'Tilgang til lokasjonen din ble avvist. Du kan gå til innstillingene for nettleseren for å gi tilgang.';
      setLocationError(errorMessage);
    } finally {
      setIsFindingLocation(false);
    }
  };

  return (
    <S.SelectGridCol s="12" m="12" l="12" xl="12">
      <TransitionGroup>
        {isSelectReady ? (
          <Transition nodeRef={selectRef} in={isSelectReady} timeout={250} unmountOnExit mountOnEnter>
            {(state) => (
              <AnimationWrapper ref={selectRef} $state={state}>
                <Text variant={TextVariant.Header3}>Se hva som er tilgjengelig i ditt område:</Text>
                <S.SelectWrapper>
                  <S.StyledSelect
                    handleSelect={handleSelect}
                    options={options}
                    maxLines={6}
                    width="100%"
                    selected={
                      centerId
                        ? {
                            value: centerId.toString(),
                            label: `${selectedCenterWithDistance?.title || ''}${formatDistance(selectedCenterWithDistance?.distanceInMeters)}`,
                          }
                        : undefined
                    }
                    placeholder="Velg ditt NAF senter"
                  />
                  {data && !error && !isUpdating && centerId ? (
                    <S.ButtonContainer>
                      <Button
                        textWrap="nowrap"
                        color="secondary"
                        to={`/${slug}/naf-senter/${selectedCenter?.link.data.slug}`}
                        onClick={() =>
                          trackCTA({
                            cta_position: 'center_select',
                            text: `Gå til ${selectedCenter?.title}`,
                            url: `/${slug}/naf-senter/${selectedCenter?.link.data.slug}`,
                          })
                        }
                      >
                        {`Gå til ${selectedCenter?.title}`}
                        <ArrowForward width={18} height={18} />
                      </Button>
                    </S.ButtonContainer>
                  ) : (
                    <S.ButtonContainer>
                      <Button
                        textWrap="nowrap"
                        startIcon={PinDrop}
                        variant="outlined"
                        onClick={handleFindNearestCenter}
                        disabled={isFindingLocation}
                      >
                        {isFindingLocation ? 'Finner nærmeste senter...' : 'Finn nærmeste NAF Senter'}
                      </Button>
                    </S.ButtonContainer>
                  )}
                </S.SelectWrapper>
              </AnimationWrapper>
            )}
          </Transition>
        ) : (
          <Transition nodeRef={selectRef} in={!isSelectReady} timeout={250} unmountOnExit mountOnEnter>
            {(state) => (
              <AnimationWrapper ref={selectRef} $state={state}>
                <SelectLoader />
              </AnimationWrapper>
            )}
          </Transition>
        )}
      </TransitionGroup>
      {error && (
        <UserInteraction
          maxWidth={600}
          title="Oops, noe gikk galt!"
          variant={UserInteractionVariant.Error}
          button={{
            onClick: () => {
              dispatch(actions.getCenters.request(undefined, undefined));
            },
            children: 'Prøv igjen',
            type: 'button',
            isLoading: isUpdating,
          }}
        />
      )}
      {locationError && (
        <S.LocationErrorContainer>
          <UserInteraction
            maxWidth={600}
            title={locationError}
            variant={UserInteractionVariant.Warning}
            button={{
              onClick: () => {
                setLocationError(null);
              },
              children: 'Lukk',
              type: 'button',
            }}
          />
        </S.LocationErrorContainer>
      )}
    </S.SelectGridCol>
  );
};

export default CenterSelect;
